import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useAdminConfirm } from '@/admin/shared/AdminConfirmProvider';

/**
 * While the component using it is mounted, asks before the admin leaves the page: another
 * page in the app asks with the admin confirm dialog (needs AdminConfirmProvider), and
 * closing or reloading the tab asks with the browser's own dialog. Returns allowLeave(),
 * to call just before leaving on purpose (e.g. to the import's report once it starts).
 */
export const useLeaveGuard = ({ title, content }) => {
	const confirm = useAdminConfirm();
	const isLeavingRef = useRef(false);
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			!isLeavingRef.current && currentLocation.pathname !== nextLocation.pathname,
	);

	useEffect(() => {
		if (blocker.state !== 'blocked') return;
		const ask = async () => {
			const confirmed = await confirm({ title, content, confirmText: 'Leave' });
			if (confirmed) {
				blocker.proceed();
			} else {
				blocker.reset();
			}
		};
		ask();
	}, [blocker, confirm, title, content]);

	useEffect(() => {
		const handleBeforeUnload = (event) => {
			if (!isLeavingRef.current) {
				event.preventDefault();
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, []);

	return () => {
		isLeavingRef.current = true;
	};
};
